import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CaretDown, CaretRight, Camera, Plus, Factory } from '@phosphor-icons/react';
import { apiFormaleta, medidasDe } from '@/api/formaleta';
import type { ModeloFormaleta, RenglonFormaleta } from '@/api/formaleta';
import { Aviso, Conteo, Fecha, Foto, Pastilla, SinRegistrar } from './comunes';
import { TarjetaPieza } from './TarjetaPieza';

// La tarjeta del nivel MODELO, con despliegue. Pedido textual de Daniel:
// «si le doy clic a esta agrupada que me pueda desplegar ya como internamente
// todas las que hay».
//
// El despliegue muestra los dos niveles de abajo —los RENGLONES reales (lo
// comprado en cada pedido) y las PIEZAS de cada uno— porque son hechos
// distintos: un mismo modelo puede venir del 1er y del 2do pedido
// (`CR-90X120SADCK` = 27 + 27 = 54) y eso es trazabilidad con el fabricante,
// no ruido.
//
// El detalle se pide SOLO al abrir (`enabled: abierto`): con 404 modelos en la
// grilla, traerlo de entrada serían 404 consultas para pintar una pantalla.

export function TarjetaModelo({ modelo, piezasHabilitadas, motivo, onInventariar }: {
  modelo: ModeloFormaleta;
  piezasHabilitadas: boolean;
  motivo: string | null;
  onInventariar: (modelo: ModeloFormaleta, idForm?: number) => void;
}) {
  const [abierto, setAbierto] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['formaleta', 'modelo', modelo.nombre],
    queryFn: () => apiFormaleta.modelo(modelo.nombre),
    enabled: abierto,
  });

  const medidas = medidasDe(modelo);

  return (
    <article className="overflow-hidden rounded-lg border border-brand-gray-200 bg-white">
      <div className="flex items-stretch">
        <span
          style={{ backgroundColor: modelo.colorHex || '#D9D9D9' }}
          className="w-2 shrink-0"
        />

        <button
          type="button"
          onClick={() => setAbierto(v => !v)}
          // py-3 y no py-1: la pantalla se usa con un pulgar y guante.
          className="flex min-w-0 flex-1 items-center gap-3 p-3 text-left hover:bg-brand-bg"
        >
          <Foto url={modelo.urlFoto} alt={modelo.nombre} />

          <div className="min-w-0 flex-1">
            {/* La identidad de campo: abreviatura + medidas. El código del
                fabricante va abajo y en mono — prohibición expresa de mostrarlo
                como identificador principal. */}
            <div className="flex flex-wrap items-center gap-2">
              {modelo.abreviatura
                ? <Pastilla texto={modelo.abreviatura} colorHex={modelo.colorHex}
                    titulo={modelo.categoria || undefined} grande />
                : <SinRegistrar que="sin categoría"
                    detalle="Son los accesorios: 184 renglones y ≈17.600 piezas fuera del catálogo de categorías." />}
              <span className="text-sm font-bold text-brand-gray-500">
                {medidas || <SinRegistrar que="sin medidas" />}
              </span>
              {modelo.fabPropia && (
                <span title="Fabricado por Adelante (Pedido AD01)"
                  className="flex items-center gap-1 rounded bg-brand-green-100/25 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand-gray-500">
                  <Factory size={11} weight="bold" /> propia
                </span>
              )}
            </div>

            <p className="mt-1 truncate font-mono text-[11px] text-brand-gray-400"
              title={modelo.nombre}>
              {modelo.nombre}
            </p>

            <p className="mt-1 text-[11px] text-brand-gray-400">
              {modelo.categoria || 'accesorio sin clasificar'}
              {modelo.renglones > 1 && ` · ${modelo.renglones} renglones`}
              {modelo.pedidos.length > 0 && ` · ${modelo.pedidos.join(', ')}`}
            </p>
          </div>

          <Conteo
            inventariadas={modelo.piezasInventariadas}
            declaradas={modelo.piezasDeclaradas}
            habilitado={piezasHabilitadas}
          />

          <span className="shrink-0 text-brand-gray-300">
            {abierto ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
          </span>
        </button>
      </div>

      {abierto && (
        <div className="border-t border-brand-gray-100 bg-brand-bg p-3">
          {isLoading && <p className="text-xs text-brand-gray-400">Abriendo el modelo…</p>}

          {!piezasHabilitadas && (
            <div className="mb-3">
              <Aviso
                tono="alerta"
                titulo="Las piezas individuales todavía no se pueden registrar"
                detalle={motivo}
                pista="Mientras tanto se ven los renglones comprados y la cantidad declarada, que es lo único que existe en la base."
              />
            </div>
          )}

          {data?.renglones.map(r => (
            <Renglon
              key={r.idForm}
              renglon={r}
              nombreModelo={modelo.nombre}
              piezasHabilitadas={piezasHabilitadas}
              onInventariar={() => onInventariar(modelo, r.idForm)}
            />
          ))}
        </div>
      )}
    </article>
  );
}

// ── El RENGLÓN: lo comprado en un pedido ────────────────────────────────────

function Renglon({ renglon, nombreModelo, piezasHabilitadas, onInventariar }: {
  renglon: RenglonFormaleta;
  nombreModelo: string;
  piezasHabilitadas: boolean;
  onInventariar: () => void;
}) {
  const qc = useQueryClient();
  const archivo = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const subir = useMutation({
    mutationFn: (f: File) => apiFormaleta.fotoRenglon(renglon.idForm, f),
    onSuccess: (r) => {
      setAviso(r.advertencia);
      // Invalida la grilla y el detalle: la foto del modelo es la del primer
      // renglón que tenga una, así que la tarjeta de arriba también cambia.
      qc.invalidateQueries({ queryKey: ['formaleta', 'modelos'] });
      qc.invalidateQueries({ queryKey: ['formaleta', 'modelo', nombreModelo] });
    },
    onError: (e) => setAviso((e as Error).message),
  });

  return (
    <section className="mb-3 rounded-lg border border-brand-gray-200 bg-white p-3 last:mb-0">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => archivo.current?.click()}
          title="Tomar o elegir la foto de este modelo"
          className="relative shrink-0"
        >
          <Foto url={renglon.urlFoto} alt={renglon.nombre || 'renglón'} lado={56} />
          <span className="absolute -bottom-1 -right-1 rounded-full bg-brand-gray-500 p-1 text-white">
            <Camera size={10} weight="bold" />
          </span>
        </button>
        <input
          ref={archivo}
          type="file"
          accept="image/*"
          // `environment` abre la cámara trasera directo en el teléfono: la
          // foto se toma en la bodega, no se busca en la galería.
          capture="environment"
          hidden
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) subir.mutate(f);
            e.target.value = '';
          }}
        />

        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-brand-gray-500">
            {renglon.pedido || <SinRegistrar que="sin pedido" />}
            {renglon.fabPropia && ' · Adelante'}
          </p>
          <p className="mt-0.5 truncate font-mono text-[11px] text-brand-gray-400"
            title={renglon.idFab || undefined}>
            {renglon.idFab || 'sin código de fabricante'}
          </p>
          <p className="mt-0.5 flex items-center gap-2 text-[11px] text-brand-gray-400">
            {renglon.fabricante || <SinRegistrar que="sin fabricante" />}
            <Fecha valor={renglon.fechaCreacion} que="sin fecha" />
          </p>
        </div>

        <Conteo
          inventariadas={renglon.piezasInventariadas}
          declaradas={renglon.piezasDeclaradas}
          habilitado={piezasHabilitadas}
        />

        {piezasHabilitadas && (
          <button
            type="button"
            onClick={onInventariar}
            className="flex shrink-0 items-center gap-1 rounded bg-brand-green-100 px-3 py-2 text-xs font-bold text-black hover:bg-brand-green-200"
          >
            <Plus size={13} weight="bold" /> Inventariar
          </button>
        )}
      </div>

      {subir.isPending && (
        <p className="mt-2 text-[11px] text-brand-gray-400">Subiendo la foto…</p>
      )}
      {aviso && (
        <p className="mt-2 text-[11px] font-semibold text-brand-red-200">{aviso}</p>
      )}

      {renglon.piezas.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-brand-gray-100 pt-3">
          {renglon.piezas.map(p => (
            <TarjetaPieza key={p.id} pieza={p} compacta />
          ))}
        </div>
      )}

      {piezasHabilitadas && renglon.piezas.length === 0 && (
        // Rotulado, no vacío: estas son las piezas que Daniel describió —
        // «hay muchas que se han fabricado y nunca se han inventariado».
        <p className="mt-3 border-t border-brand-gray-100 pt-3 text-[11px] font-semibold text-brand-red-200">
          Ninguna de las {renglon.piezasDeclaradas} piezas de este renglón está
          inventariada una por una todavía.
        </p>
      )}
    </section>
  );
}
